/**
 * Activity & notifications — DESIGN.md §6.10, §7.5.
 */
import { useEffect, useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ActivityEvent, Notification, Page } from '@slate/shared';
import { http } from '@/lib/api';
import { queryKeys } from '@/lib/keys';

export function useTaskActivity(taskId: string | null) {
  return useInfiniteQuery({
    queryKey: queryKeys.taskActivity(taskId ?? 'none'),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      http.get<Page<ActivityEvent>>(`/tasks/${taskId!}/activity`, {
        query: { cursor: pageParam, limit: 100 },
      }),
    getNextPageParam: (last) => last.cursor.next,
    enabled: Boolean(taskId),
  });
}

export function useNotifications(unreadOnly = false) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.notifications, { unreadOnly }] as const,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      http.get<Page<Notification>>('/notifications', {
        query: { cursor: pageParam, limit: 50, unreadOnly: String(unreadOnly) },
      }),
    getNextPageParam: (last) => last.cursor.next,
  });
}

/**
 * The sidebar badge. §15: "if SSE is unavailable, the app remains fully
 * functional (Query's normal refetch-on-focus + 60 s polling for the Inbox
 * badge)" — so the poll is the floor, and SSE just makes it feel instant.
 */
export function useUnreadCount(): UseQueryResult<{ unread: number }, Error> {
  return useQuery({
    queryKey: queryKeys.unreadCount,
    queryFn: () => http.get<{ unread: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    // #52 — `taskId` marks everything unread about one card, which is what
    // opening the card means. The Inbox does not have to be loaded for it.
    mutationFn: (input: { ids?: number[]; taskId?: string; all?: boolean }) =>
      http.post<{ unread: number }>('/notifications/read', {
        ...input,
        all: input.all ?? false,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications }),
        queryClient.invalidateQueries({ queryKey: queryKeys.unreadCount }),
      ]);
    },
  });
}

/**
 * #52 — reading a card reads its notifications.
 *
 * "When i get a new notification in the inbox, i can click and read the new
 * information, it doesn't update the inbox when i read this like in Asana."
 * Both halves of that: the Inbox marks a row read when you follow it, and this
 * covers the other half — arriving at the card by any route at all.
 *
 * Gated on there being something unread anywhere, so the ordinary case (open a
 * card, nothing is unread) costs no request. The count is already polled for
 * the sidebar badge, so the gate is free. It fires once per card: a refetch
 * that lowers the count must not re-arm it, or every card open would post twice.
 */
export function useMarkCardRead(taskId: string | null): void {
  const { data } = useUnreadCount();
  // `mutate` is stable across renders; the mutation object around it is not,
  // so it is the function that goes in the dependency list.
  const { mutate } = useMarkRead();
  const done = useRef<string | null>(null);
  const unread = data?.unread ?? 0;

  useEffect(() => {
    if (!taskId || unread === 0) return;
    // Once per card. The count drops as a result of this call, and without the
    // guard the refetch that reports the new count would run it again.
    if (done.current === taskId) return;
    done.current = taskId;
    mutate({ taskId });
  }, [taskId, unread, mutate]);
}

/**
 * The admin audit log — §6.10, card #25.
 *
 * Filters ride in the query key so switching them is a new query rather than a
 * refetch of the old one, which keeps the previous page on screen instead of
 * blanking it. Empty values are dropped so the key for "no filters" is stable.
 */
export interface AuditFilters {
  actorId?: string;
  /** #76 — `user` | `agent` | `automation` | `system`. */
  actorType?: string[];
  eventType?: string[];
  entityType?: string[];
  from?: string;
  to?: string;
}

export function useAuditLog(filters: AuditFilters) {
  const query: Record<string, string> = {};
  if (filters.actorId) query.actorId = filters.actorId;
  if (filters.actorType?.length) query.actorType = filters.actorType.join(',');
  if (filters.eventType?.length) query.eventType = filters.eventType.join(',');
  if (filters.entityType?.length) query.entityType = filters.entityType.join(',');
  if (filters.from) query.from = filters.from;
  if (filters.to) query.to = filters.to;

  return useInfiniteQuery({
    queryKey: ['admin', 'audit', query] as const,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      http.get<Page<ActivityEvent>>('/admin/audit', {
        query: { ...query, cursor: pageParam, limit: 50 },
      }),
    getNextPageParam: (last) => last.cursor.next,
  });
}
